// F-01 · Registro del caso. HU-01 a HU-08 y HU-36.
//
// Cinco endpoints, que son TODOS los que el Sprint 1 expone para casos:
//
//   GET  /casos/{id}                 releer el caso guardado (HU-01, retomar)
//   POST /casos                      abrir el borrador (HU-01)
//   PUT  /casos/{id}                 guardar secciones (HU-02..HU-06, HU-36)
//   POST /casos/{id}/adjuntos        asociar evidencia ya subida (HU-07)
//   POST /casos/{id}/enviar          enviar (HU-08)
//   GET  /casos/configuracion-de-adjuntos   limites vigentes (HU-07 + HU-31)
//
// El `GET` devuelve todo MENOS la seccion 7 (afectacion a personas): es dato L2 y
// leerlo exige elevar el rol y dejar asiento `lectura_l2` (RNF-03), cosa que esa
// ruta no hace. `tiene_dato_de_personas` avisa de que la seccion existe, para
// poder decirselo al usuario en vez de mostrarla vacia como si nadie la hubiera
// llenado.

import { DatosInvalidos, pedir, segPais, segUuid } from './cliente';
import type { RespuestaDelApi } from '../tipos';

/**
 * HU-01: retomar un borrador y seguir donde se dejo.
 *
 * Devuelve `version_bloqueo`, que es con lo que arranca el bloqueo optimista de
 * HU-36: sin ella no se puede volver a guardar.
 */
export function verCaso(casoId: string) {
  return pedir<RespuestaDelApi>(`/casos/${segUuid(casoId)}`);
}

/** HU-01. Abre el caso. No genera numero ni expediente ni avisos (RN-01). */
type AperturaDeCaso = {
  paisEvento: string;
  fechaRecepcion: string;
  criterioPreliminar?: string;
  declaracionResponsable?: string;
};

export function abrirBorrador({ paisEvento, fechaRecepcion, criterioPreliminar, declaracionResponsable }: AperturaDeCaso) {
  return pedir('/casos', {
    metodo: 'POST',
    cuerpo: {
      pais_evento: paisEvento,
      fecha_recepcion: fechaRecepcion,
      // Dominio cerrado: 'grave' | 'no_grave'. "Prefiero no opinar" se manda como
      // ausencia del campo, NO como cadena vacia: el backend responde 422 ante
      // cualquier otro texto.
      ...(criterioPreliminar ? { criterio_preliminar: criterioPreliminar } : {}),
      ...(declaracionResponsable ? { declaracion_responsable: declaracionResponsable } : {}),
    },
  });
}

/**
 * HU-02 a HU-06 y HU-36. Guarda una o varias secciones.
 *
 * Los campos ausentes NO se tocan — es lo que permite que el autoguardado mande
 * solo la seccion que cambio en vez del formulario de sesenta campos. Por eso este
 * modulo construye el cuerpo omitiendo lo indefinido en vez de mandar `null`:
 * mandar `null` significaria "borra esto".
 */
export function guardarBorrador(casoId: string, versionEsperada: number, secciones: Record<string, unknown>) {
  // `Record<string, unknown>` y no un objeto literal: abajo se le agregan claves con
  // `cuerpo[clave]`, y un literal cerrado no admite indexado por string.
  const cuerpo: Record<string, unknown> = { version_esperada: versionEsperada };
  for (const [clave, valor] of Object.entries(secciones)) {
    if (valor !== undefined) cuerpo[clave] = valor;
  }
  return pedir(`/casos/${segUuid(casoId)}`, { metodo: 'PUT', cuerpo });
}

/** HU-07. El binario NO pasa por la API (ADR-015): aqui solo viaja su referencia. */
type AdjuntoAAsociar = {
  nombreArchivo: string;
  rutaBlob: string;
  huellaSha256: string;
  tipoMime: string;
  tamanoBytes: number;
  contieneDatoDePersonas?: boolean;
};

export function asociarAdjunto(
  casoId: string,
  { nombreArchivo, rutaBlob, huellaSha256, tipoMime, tamanoBytes, contieneDatoDePersonas = false }: AdjuntoAAsociar,
) {
  return pedir(`/casos/${segUuid(casoId)}/adjuntos`, {
    metodo: 'POST',
    cuerpo: {
      nombre_archivo: nombreArchivo,
      ruta_blob: rutaBlob,
      huella_sha256: huellaSha256,
      tipo_mime: tipoMime,
      tamano_bytes: tamanoBytes,
      // NO se omitia por descuido: sin el, el campo toma `false` por defecto y la fila
      // dice que el adjunto NO tiene dato de personas mientras el BLOB esta en el
      // contenedor `pii-5a`. Es una discrepancia que no da error hoy y la da en el
      // ano 5: la purga mira la fila, no el contenedor (ADR-004, RNF-07).
      contiene_dato_de_personas: contieneDatoDePersonas,
    },
  });
}

/**
 * HU-07 · quitar una evidencia del borrador.
 *
 * `DELETE /casos/{id}/adjuntos/{huella}`. Responde 200 con el borrador entero —no 204—
 * porque al quitar la ULTIMA evidencia el caso vuelve a ser no-enviable (control C-01) y
 * el panel de "faltan N datos" tiene que enterarse en el acto.
 *
 * Lo que desaparece es el VINCULO con el caso, no el binario: el contenedor es WORM y no
 * admite borrado. Es lo que la HU pide y lo que la auditoria necesita.
 *
 * La huella se valida como sha-256 antes de meterla en la ruta: es texto que llega de la
 * lista y termina en una URL.
 */
export function quitarAdjunto(casoId: string, huellaSha256: string) {
  const huella = String(huellaSha256 ?? '');
  if (!/^[0-9a-f]{64}$/i.test(huella)) {
    throw new DatosInvalidos({
      detalle: `Huella no válida para una ruta: ${JSON.stringify(huella)} no es un sha-256`,
    });
  }
  // Responde el BORRADOR ENTERO con su `version_bloqueo` (el caso de uso termina en
  // `casos.guardar(caso)` y el trigger la incrementa): quien llama la necesita para
  // adoptar la version nueva. Se tipa lo minimo que se usa.
  return pedir<{ version_bloqueo?: number } | null>(`/casos/${segUuid(casoId)}/adjuntos/${encodeURIComponent(huella)}`, {
    metodo: 'DELETE',
  });
}

/**
 * HU-08. La frontera de RN-01: antes de esto no hay numero, ni expediente, ni avisos.
 *
 * `duplicadoAdvertidoAceptado` implementa el "elijo continuar" de HU-07: el primer
 * intento va sin el y puede responder 409 con candidatos; el segundo lo lleva en
 * `true` porque el usuario ya los vio y decidio.
 */
export function enviarCaso(casoId: string, { duplicadoAdvertidoAceptado = false }: { duplicadoAdvertidoAceptado?: boolean } = {}) {
  return pedir(`/casos/${segUuid(casoId)}/enviar`, {
    metodo: 'POST',
    cuerpo: { duplicado_advertido_aceptado: duplicadoAdvertidoAceptado },
  });
}

/**
 * HU-07 + HU-31. Los limites salen de `parametro`, no del codigo del front.
 *
 * Se consultan para PRE-validar en pantalla y evitar que el usuario suba un archivo
 * que el servidor va a rechazar. La validacion de verdad sigue siendo del servidor:
 * esto es cortesia, no una guarda.
 */
export function configuracionDeAdjuntos() {
  return pedir('/casos/configuracion-de-adjuntos');
}

/**
 * El numero de notificacion que la pantalla MUESTRA mientras el caso es borrador.
 *
 * Sale de `serie_correlativo`, que es donde vive RN-07. Antes se calculaba en el
 * navegador a partir de los casos que hubiera cargados en memoria; como el registro
 * conectado no carga ninguno, el campo mostraba `PE-001-2026` con la serie de Peru
 * en 73 — un numero inventado en el unico campo que el usuario no puede corregir.
 *
 * ⚠ Es una PREVISION, no una reserva. Si otro registrador del mismo pais envia antes,
 * el numero definitivo sera el siguiente. El de verdad llega en la respuesta de
 * `enviarCaso()` (RN-01: el numero nace con el envio). No guardarlo como si fuera el
 * identificador del caso.
 *
 * `segPais` y no interpolacion directa (T-45, jssecurity:S8476): este `pais` sale del
 * selector del formulario o del usuario guardado en `sessionStorage`, es decir de la
 * eleccion de una persona, y era el UNICO valor de todo `src/api` que entraba crudo
 * en una URL. El backend exige `^[A-Z]{2}$` en esa ruta; ahora se comprueba antes de
 * salir, y `pe` o `undefined` fallan aqui en vez de gastar un viaje para un 422.
 */
export function proximoNumeroDeNotificacion(pais: string) {
  return pedir<{ proximo_numero: string }>(`/casos/series/${segPais(pais)}`);
}

/**
 * T-37 (MG-ILE-471) · La bandeja: los casos del registrador, mas recientes primero.
 *
 * `GET /casos` devuelve un ENVOLTORIO `{ casos, total }` y no un array plano: el
 * total lo cuenta el servidor, y contar la lista en el cliente mentiria en cuanto
 * `limite` recorte la pagina.
 *
 * Cada fila es un RESUMEN (numero, estado, notificante, producto principal,
 * conteos) pensado para pintar la tabla sin abrir caso por caso. Para retomar un
 * borrador se pide el caso completo con `verCaso`: la fila trae `version_bloqueo`
 * solo como referencia, la que manda al guardar es la del `GET` del caso.
 *
 * `estado` filtra por uno de los estados del dominio ('borrador', 'registrado'…);
 * cualquier otro valor es un 422 de contrato, no un filtro vacio. `limite` acota
 * la pagina (el servidor pone el tope y el defecto: 50, maximo 200). Ambos se
 * omiten de la URL cuando no se piden, para que el servidor aplique los suyos.
 */
type FiltroDeCasos = {
  /** `borradores`, `enviados` o `todos`. Cualquier otro valor es un 422 de contrato. */
  estado?: string;
  limite?: number;
  pagina?: number;
};

/** La pagina de la bandeja: los casos y el total, para la paginacion. */
export type PaginaDeCasos = { casos: Record<string, unknown>[]; total: number };

export function listarCasos({ estado, limite, pagina }: FiltroDeCasos = {}) {
  const parametros = new URLSearchParams();
  if (estado) parametros.set('estado', estado);
  if (limite) parametros.set('limite', String(limite));
  // Solo desde la 2: la pagina 1 es el default del servidor y omitirla mantiene
  // las URLs de siempre (y las pruebas que fijan su forma) sin ruido.
  if (pagina && pagina > 1) parametros.set('pagina', String(pagina));
  const consulta = parametros.toString();
  return pedir<PaginaDeCasos>(consulta ? `/casos?${consulta}` : '/casos');
}
